import React from "react";
import { dashed } from "../../../lib/utils/strings";
import { FieldLabel } from "../../FieldLabel";

export interface FormFieldLayoutProps {
  fieldId: string; // should be camelCase
  label: React.ReactNode;
  isRequired: boolean;
  showPlaceholderAndHideLabel?: boolean;
  errorMessage?: React.ReactNode;
  children?: React.ReactNode;
  explanation?: React.ReactNode;
  containerId?: string;
}

export const getFormFieldLabelHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-label`;
export const getFormFieldErrorHtmlId = (fieldId: string) => `sid-${dashed(fieldId)}-error`;

/**
 * In the JSLIB, a FormField has a label, inline error message, and children like a text input
 * This component provides consistent structure, classnames and some accessibility considerations for all FormFields
 */
export const FormFieldLayout = ({
  fieldId,
  label,
  errorMessage,
  isRequired,
  showPlaceholderAndHideLabel = false,
  children,
  explanation = undefined,
  containerId = undefined,
}: FormFieldLayoutProps) => {
  const labelHtmlId = getFormFieldLabelHtmlId(fieldId);
  const errorHtmlId = getFormFieldErrorHtmlId(fieldId);
  const containerClassName = containerId || `sid-${dashed(fieldId)}`;

  return (
    <div className={`sid-field ${containerClassName}`}>
      <div className="sid-l-space-top-md" />

      <FieldLabel
        text={label}
        isRequired={isRequired}
        htmlForLabel={`sid-${dashed(fieldId)}`}
        id={labelHtmlId}
        displayClasses={`${explanation ? "sid-field__label-with-explanation" : ""}${
          explanation && showPlaceholderAndHideLabel ? " " : ""
        }${showPlaceholderAndHideLabel ? "sid-h-screen-reader-only" : ""}`}
      >
        {explanation && explanation}
      </FieldLabel>

      {children}

      {errorMessage && (
        <div className="sid-field-error" id={errorHtmlId}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};
